import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { PAGE_DIMS } from './canvas/constants';
import { CanvasRuler, AlignmentGuide } from './canvas/CanvasRuler';
import { CanvasToolbar } from './canvas/CanvasToolbar';
import { PageNavigator } from './canvas/PageNavigator';
import { PageRenderer } from './canvas/PageRenderer';

// Re-exports for backward compatibility
export { PAGE_DIMS } from './canvas/constants';
export {
  useSnapPosition,
  useAlignmentGuides,
  calculatePageForPosition,
  distributeElementsToPages,
} from './canvas/hooks';

export function ReportCanvas({
  pageSize = 'letter',
  orientation = 'portrait',
  margins = { top: 48, right: 48, bottom: 48, left: 48 },
  zoom,
  onZoomChange,
  showGrid,
  onToggleGrid,
  snapSize,
  onSnapChange,
  onCanvasClick,
  pages = [{ id: 'page_1', name: 'Page 1' }],
  activePageIndex = 0,
  onPageChange,
  onAddPage,
  onDeletePage,
  onInsertPageBreak,
  headerConfig = { enabled: true, height: 60, repeatOnPages: true, showOnFirstPage: true },
  footerConfig = { enabled: true, height: 40 },
  renderPageContent,
  renderHeader,
  renderFooter,
  children,
  alignmentGuides = { x: null, y: null },
  showRulers = true,
  onToggleRulers,
  onLibraryDrop,
}) {
  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const hRulerRef = useRef(null);
  const vRulerRef = useRef(null);
  const [showPageNav, setShowPageNav] = useState(true);
  const [localShowRulers, setLocalShowRulers] = useState(showRulers);
  const [rulerOffset, setRulerOffset] = useState({ x: 0, y: 0 });

  const dims = PAGE_DIMS[pageSize] || PAGE_DIMS.letter;
  const pageW = orientation === 'portrait' ? dims.w : dims.h;
  const pageH = orientation === 'portrait' ? dims.h : dims.w;

  const contentW = pageW - (margins?.left || 48) - (margins?.right || 48);
  const contentH = pageH - (margins?.top || 48) - (margins?.bottom || 48);

  const headerH = headerConfig?.enabled ? (headerConfig.height || 60) : 0;
  const footerH = footerConfig?.enabled ? (footerConfig.height || 40) : 0;
  const bodyH = contentH - headerH - footerH;

  const zoomPercent = zoom || 75;
  const scale = zoomPercent / 100;

  const toggleRulers = () => {
    setLocalShowRulers(prev => !prev);
    if (onToggleRulers) onToggleRulers();
  };

  // Sync rulers with canvas scroll position and page offset
  const syncRulers = useCallback(() => {
    const scrollEl = canvasRef.current;
    if (!scrollEl || !localShowRulers) return;
    const pageEl = scrollEl.querySelector('[data-page-index="0"]');
    if (!pageEl) return;
    const scrollRect = scrollEl.getBoundingClientRect();
    const pageRect = pageEl.getBoundingClientRect();
    const offsetX = Math.round(pageRect.left - scrollRect.left);
    const offsetY = Math.round(pageRect.top - scrollRect.top);
    setRulerOffset(prev => (prev.x === offsetX && prev.y === offsetY) ? prev : { x: offsetX, y: offsetY });
  }, [localShowRulers]);

  useEffect(() => {
    const scrollEl = canvasRef.current;
    if (!scrollEl) return;
    const handler = () => syncRulers();
    scrollEl.addEventListener('scroll', handler, { passive: true });
    const timer = setTimeout(syncRulers, 100);
    const ro = new ResizeObserver(syncRulers);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener('scroll', handler);
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [syncRulers, activePageIndex, scale]);

  const fitToWidth = () => {
    if (scrollContainerRef.current) {
      const containerW = scrollContainerRef.current.clientWidth - (showPageNav ? 180 : 0) - 80;
      const newZoom = Math.round(containerW / pageW * 100);
      onZoomChange(Math.min(150, Math.max(25, newZoom)));
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current && pages.length > 1) {
      const pageEl = scrollContainerRef.current.querySelector(`[data-page-index="${activePageIndex}"]`);
      if (pageEl) {
        pageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activePageIndex, pages.length]);

  const pageThumbnails = useMemo(() => pages.map((page, idx) => ({
    id: page.id,
    name: page.name || `Page ${idx + 1}`,
    index: idx,
    hasPageBreak: page.hasManualBreak,
  })), [pages]);

  return (
    <div className="flex flex-col h-full" data-testid="report-canvas-container">
      <CanvasToolbar
        zoomPercent={zoomPercent} onZoomChange={onZoomChange} fitToWidth={fitToWidth}
        showGrid={showGrid} onToggleGrid={onToggleGrid}
        snapSize={snapSize} onSnapChange={onSnapChange}
        localShowRulers={localShowRulers} toggleRulers={toggleRulers}
        dimsLabel={dims.label} pageW={pageW} pageH={pageH}
        showPageNav={showPageNav} setShowPageNav={setShowPageNav}
        pages={pages} activePageIndex={activePageIndex} onPageChange={onPageChange}
        onAddPage={onAddPage} onInsertPageBreak={onInsertPageBreak}
      />

      <div ref={scrollContainerRef} className="flex-1 flex overflow-hidden">
        {showPageNav && (
          <PageNavigator
            pageThumbnails={pageThumbnails} activePageIndex={activePageIndex}
            onPageChange={onPageChange} onAddPage={onAddPage} onDeletePage={onDeletePage}
            pages={pages} pageW={pageW} pageH={pageH}
            headerConfig={headerConfig} footerConfig={footerConfig}
            headerH={headerH} footerH={footerH}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          {localShowRulers && (
            <div className="flex-shrink-0 flex">
              <div className="w-5 h-5 bg-muted/40 border-r border-b border-border/30 flex-shrink-0" />
              <div className="overflow-hidden flex-1" ref={hRulerRef}>
                <div style={{ transform: `translateX(${rulerOffset.x}px)` }}>
                  <CanvasRuler orientation="horizontal" length={pageW} scale={scale} />
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 flex overflow-hidden">
            {localShowRulers && (
              <div className="flex-shrink-0 overflow-hidden w-5" ref={vRulerRef}>
                <div style={{ transform: `translateY(${rulerOffset.y}px)` }}>
                  <CanvasRuler orientation="vertical" length={pageH} scale={scale} />
                </div>
              </div>
            )}

            <div
              ref={canvasRef}
              className="flex-1 overflow-auto bg-muted/30 flex flex-col items-center p-8 gap-6 relative"
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
              onDrop={e => { e.preventDefault(); const data = e.dataTransfer.getData('text/plain'); if (data && data.startsWith('library_visual:') && onLibraryDrop) { onLibraryDrop(data.replace('library_visual:', '')); } }}
              onClick={onCanvasClick}
              data-testid="report-canvas-scroll"
            >
              {alignmentGuides.x !== null && <AlignmentGuide position={alignmentGuides.x} orientation="vertical" scale={scale} />}
              {alignmentGuides.y !== null && <AlignmentGuide position={alignmentGuides.y} orientation="horizontal" scale={scale} />}

              {pages.map((page, pageIndex) => (
                <PageRenderer
                  key={page.id}
                  page={page} pageIndex={pageIndex}
                  activePageIndex={activePageIndex} onPageChange={onPageChange}
                  pageW={pageW} pageH={pageH} scale={scale}
                  margins={margins} contentW={contentW} contentH={contentH}
                  headerConfig={headerConfig} footerConfig={footerConfig}
                  headerH={headerH} footerH={footerH} bodyH={bodyH}
                  showGrid={showGrid} snapSize={snapSize}
                  renderHeader={renderHeader}
                  renderPageContent={renderPageContent}
                  renderFooter={renderFooter}
                  pages={pages}
                >
                  {children}
                </PageRenderer>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
